import { Pages } from 'modules/navigation/domain/enums/pages';
import type { appFeature } from 'modules/organizer/domain/interfaces/available-feature';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IMAGE_UPSCALE_FEATURE } from 'shared/constants/image-upscale-feature';
import { useFeature } from 'shared/hooks/use-feature';
import type { ImageKind } from 'shared/utils/image/upscale/constants';

/**
 * Можно ли улучшать картинки. Не булево: экрану нужно знать, рисовать ли замок и куда вести.
 */
export type ImageUpscaleAccess = { status: 'available' } | { status: 'checking' } | { status: 'locked'; feature: appFeature };

const AVAILABLE: ImageUpscaleAccess = { status: 'available' };

function useOpenAccess(): ImageUpscaleAccess {
  return AVAILABLE;
}

function useFeatureAccess(): ImageUpscaleAccess {
  const feature = IMAGE_UPSCALE_FEATURE as appFeature;
  // Все правила `useFeature` — включая поблажку супер-админу — действуют и здесь.
  const { isEnabled, isFetching } = useFeature(feature);

  return useMemo(() => {
    if (isFetching) return { status: 'checking' };
    return isEnabled ? AVAILABLE : { status: 'locked', feature };
  }, [isEnabled, isFetching, feature]);
}

/**
 * Доступ к ИИ-апскейлу — одна точка на всё приложение (`IMAGE_UPSCALE_FEATURE`).
 *
 * Реализация выбирается один раз при загрузке модуля: константа не меняется в рантайме,
 * поэтому порядок хуков стабилен, а при `null` фичи тарифа не запрашиваются вовсе.
 */
export const useImageUpscaleAccess: () => ImageUpscaleAccess = IMAGE_UPSCALE_FEATURE === null ? useOpenAccess : useFeatureAccess;

/** Проп `upscale` для поля кита (`UDFormDropzone`, `UDFormLogoField`). */
export interface ImageUpscaleFieldOption {
  kind: ImageKind;
  locked?: boolean;
  onLockedClick?: () => void;
}

/**
 * Готовый проп `upscale` для поля формы: род картинки плюс замок и переход на подписку.
 *
 * Кит не знает ни тарифов, ни `Pages` — переход передаёт форма, и чтобы пять форм не
 * повторяли одно и то же, он собран здесь. Пока доступ проверяется, подсказка «мелкая» уже
 * видна, а кнопки нет (`locked` без обработчика): показать её открытой, а через миг с замком —
 * хуже, чем на миг позже.
 */
export function useImageUpscaleFieldOption(kind: ImageKind): ImageUpscaleFieldOption {
  const access = useImageUpscaleAccess();
  const navigate = useNavigate();
  const lockedFeature = access.status === 'locked' ? access.feature : null;

  const onLockedClick = useCallback(() => {
    if (lockedFeature) navigate(Pages.SETTINGS.builders.subscription(lockedFeature));
  }, [navigate, lockedFeature]);

  return useMemo(() => {
    if (access.status === 'checking') return { kind, locked: true };
    if (access.status === 'locked') return { kind, locked: true, onLockedClick };
    return { kind };
  }, [access.status, kind, onLockedClick]);
}
